import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import useAdd from '../api/useAdd';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [resetSent, setResetSent] = useState(false);
  const [email, setEmail] = useState('');
  const { handleAdd, loading, success } = useAdd('users/reset-password');

  const initialValues = {
    email: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('validation.invalid_email'))
      .required(t('validation.required_field')),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await handleAdd({ email: values.email });
      setEmail(values.email);
      setResetSent(true);
      toast.success(t('forgot_password.success_message'));
    } catch (error) {
      toast.error(
        error.response?.data?.message || t('common.something_went_wrong')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 8,
        }}
      >
        <Typography component="h1" variant="h4" gutterBottom>
          {t('forgot_password.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          {resetSent
            ? t('forgot_password.instructions_sent')
            : t('forgot_password.subtitle')}
        </Typography>

        {resetSent ? (
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" paragraph>
                  {t('forgot_password.check_email', { email })}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('forgot_password.email_instructions')}
                </Typography>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/login"
                  sx={{ mt: 2 }}
                >
                  {t('forgot_password.back_to_login')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  errors,
                  touched,
                  isSubmitting,
                  values,
                  handleChange,
                  handleBlur,
                }) => (
                  <Form>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label={t('forgot_password.email')}
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />

                      <Button
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                      >
                        {t('forgot_password.send_reset_link')}
                      </Button>
                    </Stack>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        )}

        {!resetSent && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2">
              {t('forgot_password.remember_password')}{' '}
              <Link component={RouterLink} to="/login" variant="body2">
                {t('forgot_password.login_link')}
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ForgotPassword; 